import type { ApiResponse } from '@shared';

/**
 * Thin, typed fetch wrapper around the local API. Unwraps the { ok, data }
 * envelope and throws a structured ApiClientError on failure so calling code
 * can render friendly messages and detect "server offline".
 */

export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Array<{ path: string; message: string }>;
  readonly offline: boolean;

  constructor(
    message: string,
    opts: {
      code?: string;
      status?: number;
      details?: Array<{ path: string; message: string }>;
      offline?: boolean;
    } = {},
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.code = opts.code ?? 'unknown';
    this.status = opts.status ?? 0;
    if (opts.details) this.details = opts.details;
    this.offline = opts.offline ?? false;
  }
}

const BASE = '/api';

async function request<T>(
  path: string,
  init?: RequestInit & { rawBody?: boolean },
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, init);
  } catch {
    // Network-level failure: the local server is unreachable.
    throw new ApiClientError(
      'The local server is offline or unreachable.',
      { code: 'offline', offline: true },
    );
  }

  let body: ApiResponse<T> | null = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text) as ApiResponse<T>;
    } catch {
      body = null;
    }
  }

  if (!res.ok || !body || body.ok === false) {
    if (body && body.ok === false) {
      throw new ApiClientError(body.error.message, {
        code: body.error.code,
        status: res.status,
        ...(body.error.details ? { details: body.error.details } : {}),
      });
    }
    throw new ApiClientError(`Request failed (${res.status})`, {
      status: res.status,
    });
  }

  return body.data;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),
  post: <T>(path: string, data?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: data === undefined ? undefined : JSON.stringify(data),
    }),
  put: <T>(path: string, data?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: data === undefined ? undefined : JSON.stringify(data),
    }),
  del: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
  /** Multipart form upload (images). */
  upload: <T>(path: string, form: FormData): Promise<T> =>
    request<T>(path, { method: 'POST', body: form }),
};

export interface HealthResponse {
  status: 'ok' | 'degraded';
  version: string;
  database: 'connected' | 'unavailable';
  features: { ocr: boolean; calculationHistory: boolean };
  time: string;
}

export function getHealth(): Promise<HealthResponse> {
  return api.get<HealthResponse>('/health');
}
